import { Dialog } from '@headlessui/react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { useEffect, useState } from 'react';
import api from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';
import ErrorDisplay from './ErrorDisplay';

export default function GoverningBodyFormModal({ isOpen, onClose, onSubmit, initialValues = null }) {
  const isEditing = !!initialValues;
  const [parentGoverningBodies, setParentGoverningBodies] = useState([]);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { t } = useLanguage();
  
  useEffect(() => {
    if (isOpen) {
      // Clear error when modal opens
      setError(null);
      
      // Fetch governing bodies for parent selection
      api.get('/api/governing-bodies')
        .then(response => {
          // Filter out current governing body if editing to prevent self-referencing
          const filtered = response.data.filter(gb => 
            !isEditing || gb.id !== initialValues?.id
          );
          setParentGoverningBodies(filtered);
        })
        .catch(error => console.error('Error fetching governing bodies:', error));
    }
  }, [isOpen, initialValues, isEditing]);

  const validationSchema = Yup.object({
    name: Yup.string().required(t('forms.required')),
    email: Yup.string().email(t('forms.invalidEmail')).required(t('forms.required')),
    phone: Yup.string(),
    address: Yup.string(),
    description: Yup.string(),
    parentGoverningBodyId: Yup.number().nullable(),
    isActive: Yup.boolean(),
    receiveMonthlyReports: Yup.boolean(),
    reportEmails: Yup.string().nullable(),
    reportScheduleDay: Yup.number().min(1).max(31).nullable(),
    reportScheduleTime: Yup.string().nullable(),
    reportTimezone: Yup.string().nullable()
  });

  const initialFormValues = initialValues || {
    name: '',
    email: '',
    phone: '',
    address: '',
    description: '',
    parentGoverningBodyId: '',
    isActive: true,
    receiveMonthlyReports: false,
    reportEmails: '',
    reportScheduleDay: 1,
    reportScheduleTime: '08:00',
    reportTimezone: 'Africa/Tripoli'
  };

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    setError(null);
    setIsSubmitting(true);
    
    try {
      // Create a copy of values to modify for submission
      const submitData = { ...values };
      
      // Convert empty parentGoverningBodyId to null
      if (!submitData.parentGoverningBodyId) {
        submitData.parentGoverningBodyId = null;
      }
      
      // Call the parent component's onSubmit with properly formatted data
      await onSubmit(submitData);
      setError(null);
      handleClose();
    } catch (submitError) {
      console.error('Form submission error:', submitError);
      setError(submitError);
    } finally {
      setSubmitting(false);
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
      
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="mx-auto max-w-2xl rounded bg-white p-6 w-full max-h-[90vh] overflow-y-auto">
          <Dialog.Title className="text-lg font-medium leading-6 text-gray-900 mb-4">
            {isEditing ? t('governingBodies.editGoverningBody') : t('governingBodies.addGoverningBody')}
          </Dialog.Title>

          {error && (
            <ErrorDisplay 
              error={error} 
              onDismiss={() => setError(null)} 
              className="mb-6"
            />
          )}
          
          <Formik
            initialValues={initialFormValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            enableReinitialize={true}
          >
            {({ errors, touched, values }) => (
              <Form className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                    {t('governingBodies.name')} *
                  </label>
                  <Field
                    id="name"
                    name="name"
                    className={`mt-1 input ${errors.name && touched.name ? 'border-red-500' : ''}`}
                  />
                  {errors.name && touched.name && (
                    <p className="mt-1 text-xs text-red-500">{errors.name}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                    {t('governingBodies.email')} *
                  </label>
                  <Field
                    id="email"
                    name="email"
                    type="email"
                    className={`mt-1 input ${errors.email && touched.email ? 'border-red-500' : ''}`}
                  />
                  {errors.email && touched.email && (
                    <p className="mt-1 text-xs text-red-500">{errors.email}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                    {t('governingBodies.phone')}
                  </label>
                  <Field
                    id="phone"
                    name="phone"
                    className={`mt-1 input ${errors.phone && touched.phone ? 'border-red-500' : ''}`}
                  />
                  {errors.phone && touched.phone && (
                    <p className="mt-1 text-xs text-red-500">{errors.phone}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="address" className="block text-sm font-medium text-gray-700">
                    {t('governingBodies.address')}
                  </label>
                  <Field
                    as="textarea"
                    id="address"
                    name="address"
                    rows={2}
                    className={`mt-1 input ${errors.address && touched.address ? 'border-red-500' : ''}`}
                  />
                  {errors.address && touched.address && (
                    <p className="mt-1 text-xs text-red-500">{errors.address}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                    {t('governingBodies.description')}
                  </label>
                  <Field
                    as="textarea"
                    id="description"
                    name="description"
                    rows={3}
                    className={`mt-1 input ${errors.description && touched.description ? 'border-red-500' : ''}`}
                  />
                  {errors.description && touched.description && (
                    <p className="mt-1 text-xs text-red-500">{errors.description}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="parentGoverningBodyId" className="block text-sm font-medium text-gray-700">
                    {t('governingBodies.parentGoverningBody')}
                  </label>
                  <Field
                    as="select"
                    id="parentGoverningBodyId"
                    name="parentGoverningBodyId"
                    className={`mt-1 input ${errors.parentGoverningBodyId && touched.parentGoverningBodyId ? 'border-red-500' : ''}`}
                  >
                    <option value="">{t('governingBodies.noParent')}</option>
                    {parentGoverningBodies.map(gb => (
                      <option key={gb.id} value={gb.id}>
                        {gb.name}
                      </option>
                    ))}
                  </Field>
                  {errors.parentGoverningBodyId && touched.parentGoverningBodyId && (
                    <p className="mt-1 text-xs text-red-500">{errors.parentGoverningBodyId}</p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    {t('governingBodies.parentHint')}
                  </p>
                </div>

                <div className="flex items-center">
                  <Field 
                    id="isActive" 
                    name="isActive" 
                    type="checkbox" 
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500" 
                  />
                  <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900">
                    {t('governingBodies.active')}
                  </label>
                </div>

                {/* Monthly Report Settings */}
                <div className="border-t pt-4 mt-4">
                  <h3 className="text-md font-semibold text-gray-900 mb-3">
                    📧 {t('governingBodies.monthlyReportSettings')}
                  </h3>
                  
                  <div className="flex items-center mb-3">
                    <Field 
                      id="receiveMonthlyReports" 
                      name="receiveMonthlyReports" 
                      type="checkbox" 
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500" 
                    />
                    <label htmlFor="receiveMonthlyReports" className="ml-2 block text-sm text-gray-900">
                      {t('governingBodies.receiveMonthlyReports')}
                    </label>
                  </div>

                  <div>
                    <label htmlFor="reportEmails" className="block text-sm font-medium text-gray-700">
                      {t('governingBodies.reportEmails')}
                    </label>
                    <Field
                      id="reportEmails"
                      name="reportEmails"
                      placeholder="email1@example.com, email2@example.com"
                      className="mt-1 input"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      {t('governingBodies.reportEmailsHelp')}
                    </p>
                  </div>

                  <div className="grid grid-cols-3 gap-3 mt-3">
                    <div>
                      <label htmlFor="reportScheduleDay" className="block text-sm font-medium text-gray-700">
                        {t('governingBodies.scheduleDay')}
                      </label>
                      <Field
                        as="select"
                        id="reportScheduleDay"
                        name="reportScheduleDay"
                        className="mt-1 input"
                      >
                        {[...Array(31)].map((_, i) => (
                          <option key={i + 1} value={i + 1}>{i + 1}</option>
                        ))}
                      </Field>
                    </div>

                    <div>
                      <label htmlFor="reportScheduleTime" className="block text-sm font-medium text-gray-700">
                        {t('governingBodies.scheduleTime')}
                      </label>
                      <Field
                        type="time"
                        id="reportScheduleTime"
                        name="reportScheduleTime"
                        className="mt-1 input"
                      />
                    </div>

                    <div>
                      <label htmlFor="reportTimezone" className="block text-sm font-medium text-gray-700">
                        {t('governingBodies.timezone')}
                      </label>
                      <Field
                        as="select"
                        id="reportTimezone"
                        name="reportTimezone"
                        className="mt-1 input"
                      >
                        <option value="Africa/Tripoli">Libya (Tripoli)</option>
                        <option value="Africa/Cairo">Egypt (Cairo)</option>
                        <option value="Europe/London">UK (London)</option>
                        <option value="America/New_York">US (New York)</option>
                      </Field>
                    </div>
                  </div>
                </div>

                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={handleClose}
                    disabled={isSubmitting}
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        {isEditing ? t('common.updating') : t('common.creating')}...
                      </>
                    ) : (
                      isEditing ? t('common.update') : t('common.create')
                    )}
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
} 